import React from "react";
import { Clock, CheckCircle2, RefreshCw, TrendingUp } from "lucide-react";
import { TangigMark } from "@/components/brand/TangigLogo";
import { FONT } from "@/lib/tokens";

/* ──────────────────────────────────────────────────────────────
   Le schema des trois etages, en HTML natif.

   Il remplace l'image : le texte reste net a tous les zooms, il
   est lisible par les moteurs de recherche, et il suit les tokens
   du site au lieu d'etre fige dans un fichier.

   Seules les icones des outils tiers restent des images — ce sont
   des marques, on ne les redessine pas. Le symbole TANGIG, lui,
   vient du composant de marque, pose sur une tuile blanche avec
   ses traits noirs.
   ────────────────────────────────────────────────────────────── */

const INK    = "#1D1D1F";
const GREY_1 = "#6E6E73";
const LINE   = "#E8E8ED";
/* Le teal des labels, releve sur la maquette d'origine. */
const TEAL   = "#00716F";

const CSS = `
  .tg-stack {
    font-family: ${FONT};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    width: 100%;
  }

  .tg-stack-card {
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 22px;
    padding: 30px 28px;
    text-align: center;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 14px 34px -20px rgba(0,0,0,0.16);
    box-sizing: border-box;
  }
  /* L'etage du milieu est le seul qui appartienne a TANGIG. */
  .tg-stack-card.is-core {
    border-color: rgba(0,113,111,0.35);
    text-align: left;
  }

  .tg-stack-label {
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${TEAL};
    margin: 0 0 12px;
  }

  .tg-stack-title {
    font-size: clamp(19px, 1.7vw, 22px);
    font-weight: 700;
    letter-spacing: -0.025em;
    line-height: 1.25;
    color: ${INK};
    margin: 0 0 12px;
  }

  .tg-stack-body {
    font-size: 14.5px;
    line-height: 1.6;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0;
  }

  /* ── Les tuiles d'outils ── */
  .tg-stack-tools {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
  }

  .tg-stack-tool {
    width: 58px;
    height: 58px;
    border-radius: 14px;
    background: #FFFFFF;
    border: 1px solid ${LINE};
    box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 8px 18px -12px rgba(0,0,0,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  .tg-stack-tool img {
    width: 30px;
    height: 30px;
    display: block;
  }

  /* ── L'etage TANGIG : le symbole a gauche du texte ── */
  .tg-stack-core {
    display: flex;
    align-items: center;
    gap: 22px;
  }

  /* Le symbole sur tuile blanche, traits noirs. */
  .tg-stack-mark {
    width: 76px;
    height: 76px;
    flex-shrink: 0;
    border-radius: 20px;
    background: #FFFFFF;
    border: 1px solid ${LINE};
    box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 10px 24px -14px rgba(0,0,0,0.24);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tg-stack-core-text { min-width: 0; }
  .tg-stack-core-text .tg-stack-title { margin-bottom: 10px; }

  /* ── Les quatre resultats ── */
  .tg-stack-results {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-top: 24px;
  }

  .tg-stack-result {
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 14px;
    padding: 14px 8px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 9px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 8px 18px -14px rgba(0,0,0,0.18);
  }

  .tg-stack-result svg { color: ${TEAL}; }

  .tg-stack-result span {
    font-size: 11.5px;
    line-height: 1.35;
    letter-spacing: -0.005em;
    color: ${GREY_1};
    text-align: center;
  }

  @media (max-width: 760px) {
    .tg-stack-card { padding: 26px 20px; border-radius: 20px; }
    .tg-stack-core { gap: 16px; }
    .tg-stack-mark { width: 62px; height: 62px; border-radius: 16px; }
    .tg-stack-tools { gap: 9px; }
    .tg-stack-tool { width: 50px; height: 50px; border-radius: 13px; }
    .tg-stack-tool img { width: 26px; height: 26px; }
    .tg-stack-results { grid-template-columns: repeat(2, 1fr); }
  }
`;

const TOOLS = [
  { src: "/images/outils/gmail.webp",  alt: "Messagerie" },
  { src: "/images/outils/sheets.webp", alt: "Tableur" },
  { src: "/images/outils/agenda.webp", alt: "Agenda" },
  { src: "/images/outils/crm.webp",    alt: "CRM" },
  { src: "/images/outils/forms.webp",  alt: "Formulaires" },
];

const RESULTS = [
  { Icon: Clock,        label: "Gain de temps" },
  { Icon: CheckCircle2, label: "Moins d'erreurs" },
  { Icon: RefreshCw,    label: "Processus reproductible" },
  { Icon: TrendingUp,   label: "Résultats mesurables" },
];

export default function SystemStackDiagram() {
  return (
    <div className="tg-stack">
      <style>{CSS}</style>

      <div className="tg-stack-card">
        <p className="tg-stack-label">Votre entreprise</p>
        <h3 className="tg-stack-title">Les outils que vous avez déjà</h3>
        <p className="tg-stack-body">
          Votre messagerie, vos tableurs, votre agenda, votre CRM, vos formulaires.
          <br />
          Vous ne changez rien, vous ne migrez rien.
        </p>
        <div className="tg-stack-tools">
          {TOOLS.map((t) => (
            <span className="tg-stack-tool" key={t.alt}>
              <img src={t.src} alt={t.alt} width="128" height="128" loading="lazy" decoding="async" />
            </span>
          ))}
        </div>
      </div>

      <div className="tg-stack-card is-core">
        <div className="tg-stack-core">
          <span className="tg-stack-mark">
            <TangigMark size={38} color={INK} title="TANGIG" />
          </span>
          <div className="tg-stack-core-text">
            <p className="tg-stack-label">La couche système</p>
            <h3 className="tg-stack-title">Le système TANGIG</h3>
            <p className="tg-stack-body">
              La méthode, la logique de décision, les automatisations, les procédures et
              les contrôles qui font tenir l&apos;ensemble.
            </p>
          </div>
        </div>
      </div>

      <div className="tg-stack-card">
        <p className="tg-stack-label">Ce que ça produit</p>
        <h3 className="tg-stack-title">Une opération qui tourne toute seule</h3>
        <p className="tg-stack-body">
          Moins de gestes manuels, moins d&apos;oublis, et un résultat qui se reproduit
          sans dépendre de la mémoire de quelqu&apos;un.
        </p>
        <div className="tg-stack-results">
          {RESULTS.map(({ Icon, label }) => (
            <span className="tg-stack-result" key={label}>
              <Icon size={22} strokeWidth={1.7} />
              <span>{label}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
